import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStripeEnvironmentSafe } from "@/lib/stripe";

export interface MembershipState {
  isActive: boolean;
  hasBook: boolean;
  isGrandfathered: boolean;
  status: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

const ACTIVE_STATUSES = ["active", "past_due"];

export function useMembership() {
  const { user } = useAuth();

  return useQuery<MembershipState>({
    queryKey: ["membership", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user) throw new Error("Please sign in.");
      const environment = getStripeEnvironmentSafe();

      const [subscriptionResult, bookResult, foundingResult] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("status, cancel_at_period_end, current_period_end")
          .eq("user_id", user.id)
          .eq("environment", environment)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase.rpc("has_paid_book_order", { user_uuid: user.id }),
        supabase.rpc("is_grandfathered_member", { user_uuid: user.id }),
      ]);
      const lookupError = subscriptionResult.error ?? bookResult.error ?? foundingResult.error;
      if (lookupError) throw lookupError;
      const sub = subscriptionResult.data;
      const hasBook = bookResult.data;
      const grandfathered = foundingResult.data;

      const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
      const stillInPeriod = !periodEnd || periodEnd.getTime() > Date.now();
      const isSubActive = !!sub && (
        (ACTIVE_STATUSES.includes(sub.status) && stillInPeriod) ||
        (sub.status === "canceled" && !!periodEnd && periodEnd.getTime() > Date.now())
      );

      return {
        isActive: (environment === "live" && isSubActive) || !!grandfathered,
        hasBook: !!hasBook,
        isGrandfathered: !!grandfathered,
        status: sub?.status ?? null,
        cancelAtPeriodEnd: !!sub?.cancel_at_period_end,
        currentPeriodEnd: sub?.current_period_end ?? null,
      };
    },
  });
}
